/**
 * How long after a fetch starts a focus/visibility event is ignored. Re-selecting a
 * tab fires visibilitychange AND focus, so without this the panel would fetch twice
 * for one user action.
 */
export const REFRESH_QUIET_PERIOD_MS = 5000;

/**
 * Whether a focus/visibility event should trigger a re-fetch of the credit panel.
 * Kept out of the directive so the rule is unit-testable without a DOM — the
 * directive only wires browser events to it.
 *
 * Pure predicate only — it does not claim `lastLoadStartedAtMs` itself. The caller
 * must record that claim synchronously as soon as this returns true, before
 * scheduling any async work (e.g. before `$scope.$applyAsync`). visibilitychange
 * and focus can both fire in the same synchronous tick when a tab is re-selected;
 * if the claim were deferred until the fetch actually starts, both events would
 * read the same stale timestamp, both pass the gate, and both queue a fetch.
 */
export function shouldRefreshOnFocus(
    isLoading: boolean,
    lastLoadStartedAtMs: number | null,
    nowMs: number,
): boolean {
    if (isLoading) return false;
    if (lastLoadStartedAtMs === null) return true;
    return nowMs - lastLoadStartedAtMs >= REFRESH_QUIET_PERIOD_MS;
}
